import { Box, Stack } from '@mui/joy'
import { useStore, useStoreMap } from 'effector-react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { employeeModel } from '~/entities/common/Taxipark/Employee'
import { usePermissions } from '~/entities/common/viewer'
import {
  editEmployeeModel,
  EditEmployee,
} from '~/features/common/Taxipark/Employees/EditEmployee'
import { Breadcrumbs, BreadcrumbsList } from '~/shared/ui/Breadcrumbs'
import { Button } from '~/shared/ui/Buttons'
import { Container, PageTitle, PageContainer } from '~/shared/ui/Layout'
import { NotFound } from '~/shared/ui/NotFound'
import { AuditsModal } from '~/widgets/common/AuditsModal'

export const EditEmployeePage = () => {
  const { t } = useTranslation()
  const { employeeId } = useParams<{ employeeId: string }>()
  const { canAuditsView, canUsersUpdate } = usePermissions()

  const resetPasswordPending = useStore(
    editEmployeeModel.resetPasswordFx.pending,
  )

  const isLoading = useStore(employeeModel.requestFx.pending)
  const employeeName = useStoreMap(editEmployeeModel.$employee, (employee) =>
    employee?.getName(),
  )

  const breadcrumbs: BreadcrumbsList = useMemo(
    () => [
      { titleI18n: 'employees', link: '/taxipark/employees' },
      { title: employeeName || '' },
    ],
    [employeeName],
  )

  const error = employeeModel.useEmployeeError(employeeId as string)

  if (!employeeId || error) {
    return <NotFound message={t('alert.failed to load employee')} />
  }

  return (
    <PageContainer title={t('employee card')}>
      <Container isHeader>
        <Stack direction='row' alignItems='end' justifyContent='space-between'>
          <Box>
            <Breadcrumbs list={breadcrumbs} lastLoading={isLoading} />
            <PageTitle loading={isLoading}>{employeeName}</PageTitle>
          </Box>
          <Stack direction='row' spacing={2}>
            {canAuditsView && <AuditsModal auditableId={employeeId} />}
            {canUsersUpdate && (
              <Button
                onClick={() => editEmployeeModel.resetPassword()}
                loading={resetPasswordPending}
                variant='gray'
              >
                {t('button.reset password')}
              </Button>
            )}
          </Stack>
        </Stack>
      </Container>

      <EditEmployee employeeId={employeeId} />
    </PageContainer>
  )
}
